import assignWith from 'lodash/assignWith';
import forEach from 'lodash/forEach';
import { LOGOUT_COMPLETE, UPDATE_MARKETPLACE_ITEM } from '../../constants/actionTypes';
import {
  SCHEMA_TYPE_API_KEY,
  SCHEMA_TYPE_AUDIT_LOG,
  SCHEMA_TYPE_AUTH_INFO,
  SCHEMA_TYPE_CATALOG_CONFIGS,
  SCHEMA_TYPE_CREDENTIALS,
  SCHEMA_TYPE_DATA_MAPS,
  SCHEMA_TYPE_DATA_SETS,
  SCHEMA_TYPE_DOCS,
  SCHEMA_TYPE_DOMAINS,
  SCHEMA_TYPE_FLOW_NODES,
  SCHEMA_TYPE_MARKETPLACE_ITEMS,
  SCHEMA_TYPE_NOTIFICATIONS,
  SCHEMA_TYPE_NOTIFICATION_CHANNELS,
  SCHEMA_TYPE_NOTIFICATION_SETTINGS,
  SCHEMA_TYPE_NOTIFICATION_TYPES,
  SCHEMA_TYPE_ORGS,
  SCHEMA_TYPE_PROJECTS,
  SCHEMA_TYPE_PROJECT_SOURCES,
  SCHEMA_TYPE_QUARNTINE,
  SCHEMA_TYPE_SCHEMA_SPECS,
  SCHEMA_TYPE_SINKS,
  SCHEMA_TYPE_SOURCES,
  SCHEMA_TYPE_TEAMS,
  SCHEMA_TYPE_TRANSFORMS,
  SCHEMA_TYPE_USERS,
  SCHEMA_TYPE_USER_SETTINGS,
  SCHEMA_TYPE_VENDORS,
  SCHEMA_TYPE_VENDOR_AUTH_PARAMS,
  SCHEMA_TYPE_VENDOR_ENDPOINTS,
  SCHEMA_TYPE_VENDOR_ENDPOINT_PARAMS,
} from '../../constants/schemaTypes';

const initialState = {
  [SCHEMA_TYPE_ORGS]: {},
  [SCHEMA_TYPE_USERS]: {},
  [SCHEMA_TYPE_TEAMS]: {},
  [SCHEMA_TYPE_SOURCES]: {},
  [SCHEMA_TYPE_SINKS]: {},
  [SCHEMA_TYPE_DATA_SETS]: {},
  [SCHEMA_TYPE_FLOW_NODES]: {},
  samples: {},
  [SCHEMA_TYPE_CREDENTIALS]: {},
  [SCHEMA_TYPE_DATA_MAPS]: {},

  [SCHEMA_TYPE_AUTH_INFO]: {},
  [SCHEMA_TYPE_NOTIFICATIONS]: {},
  [SCHEMA_TYPE_TRANSFORMS]: {},
  [SCHEMA_TYPE_SCHEMA_SPECS]: {},
  [SCHEMA_TYPE_API_KEY]: {},
  [SCHEMA_TYPE_DOCS]: {},

  [SCHEMA_TYPE_VENDORS]: {},
  [SCHEMA_TYPE_VENDOR_ENDPOINTS]: {},
  [SCHEMA_TYPE_VENDOR_ENDPOINT_PARAMS]: {},
  [SCHEMA_TYPE_VENDOR_AUTH_PARAMS]: {},
  [SCHEMA_TYPE_AUDIT_LOG]: {},
  [SCHEMA_TYPE_NOTIFICATION_TYPES]: {},
  [SCHEMA_TYPE_NOTIFICATION_CHANNELS]: {},
  [SCHEMA_TYPE_NOTIFICATION_SETTINGS]: {},
  [SCHEMA_TYPE_QUARNTINE]: {},
  [SCHEMA_TYPE_CATALOG_CONFIGS]: {},

  [SCHEMA_TYPE_PROJECTS]: {},
  [SCHEMA_TYPE_PROJECT_SOURCES]: {},
  [SCHEMA_TYPE_USER_SETTINGS]: {},
  [SCHEMA_TYPE_DOMAINS]: {},
  [SCHEMA_TYPE_MARKETPLACE_ITEMS]: {},
};

function mergeWithLinkedFlows(oldEntity, newEntity, action) {
  const object = shallowMerge(oldEntity, newEntity);

  const oldLinks = Array.isArray(oldEntity?.linked_flows) ? oldEntity.linked_flows : undefined;
  const newLinks = Array.isArray(newEntity?.linked_flows) ? newEntity.linked_flows : undefined;

  // If old had links and new has none/empty, preserve old
  if (oldLinks?.length && !newLinks?.length) {
    object.linked_flows = oldLinks;
    return object;
  }

  if (oldLinks?.length && newLinks?.length) {
    /**
     * ISSUE: Linked Flows Being Lost on Refresh
     *
     * Problem: When refreshing a flow, linked flows could be lost if the backend
     * response didn't include all linked_flows. This happened because:
     * - The reducer was replacing oldLinks with newLinks
     * - Backend responses might not always include all linked_flows
     * - This caused linked flows to disappear from the UI after refresh
     *
     * Solution: Always merge (union) linked_flows instead of replacing them.
     * This ensures that:
     * - If a parent flow has [flow1, flow2] and we refresh
     * - We don't lose any linked flows even if the response only has [flow1]
     * - We preserve existing linked flows and merge in any new ones
     *
     * Note: Using Set ensures no duplicates in the merged array.
     */
    object.linked_flows = Array.from(new Set([...oldLinks, ...newLinks]));
  }

  return object;
}

const mergeFunctions = {
  [SCHEMA_TYPE_DOCS]: (oldEntity, newEntity) => newEntity,
  [SCHEMA_TYPE_FLOW_NODES]: mergeWithLinkedFlows,
  [SCHEMA_TYPE_SOURCES]: mergeWithLinkedFlows,
};

export const entitiesReducer = (state = initialState, action) => {
  const { entities } = action;

  if (entities) {
    let newState = {};

    forEach(entities, (value, key) => {
      const stateSlice = state[key] || {};
      const mergeEntity = mergeFunctions[key] || shallowMerge;

      // Merge per-entity so merge functions can consult the action if needed
      const mergedSlice = { ...stateSlice };
      forEach(value, (newEntity, id) => {
        const oldEntity = stateSlice[id];
        mergedSlice[id] = mergeEntity(oldEntity, newEntity, action);
      });

      newState[key] = mergedSlice;
    });

    return { ...state, ...newState };
  }

  if (action.type === LOGOUT_COMPLETE) {
    return initialState;
  }

  if (action.type === UPDATE_MARKETPLACE_ITEM) {
    const { id, updates } = action.payload;
    const newState = {
      ...state,
      [SCHEMA_TYPE_MARKETPLACE_ITEMS]: {
        ...state[SCHEMA_TYPE_MARKETPLACE_ITEMS],
        [id]: {
          ...state[SCHEMA_TYPE_MARKETPLACE_ITEMS][id],
          ...updates,
        },
      },
    };
    return newState;
  }

  return state;
};

function shallowMerge(oldEntity, newEntity) {
  return { ...oldEntity, ...newEntity };
}

export default entitiesReducer;
